import React, { useState, useMemo, useContext } from 'react';
import { GlobalContext } from '../../../context/GlobalContext';
import useApi from '../../../hooks/useApi';
import ReportLayout from '../core/ReportLayout';
import ReportKPICards from '../core/ReportKPICards';
import DataTable from '../shared/DataTable';
import { formatCurrency } from '../shared/utils';
import { exportToPDF, exportToExcel } from '../shared/exportUtils';
import { FileText, Filter } from 'lucide-react';
import { TextInput, Select, Badge } from '@mantine/core';
import { format, isToday, differenceInHours } from 'date-fns';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const QuotesReport = () => {
  const { uinfo } = useContext(GlobalContext);
  
  // Filters
  const [selectedOperator, setSelectedOperator] = useState('all');
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch quotes data
  const { data: quotesData, isLoading: quotesLoading, refetch } = useApi({
    url: '/salesassist/cplusassist/quotesData',
    initial: { OutstandingQuotesDetails: [] }
  });

  // Fetch operators for filter
  const { data: operators = [], isLoading: operatorsLoading } = useApi({
    url: '/salesassist/cplusassist/getOperators',
    initial: []
  });

  // Get quote age category (same logic as QuoteList.js)
  const getQuoteCategory = (quoteDate) => {
    const now = new Date();
    if (!isToday(quoteDate)) return 'grey';
    const hoursDiff = differenceInHours(now, quoteDate);
    if (hoursDiff < 1) return 'green';
    if (hoursDiff < 2) return 'orange';
    return 'red';
  };

  // Process quotes data
  const processedData = useMemo(() => {
    if (!quotesData || !quotesData.OutstandingQuotesDetails || !Array.isArray(quotesData.OutstandingQuotesDetails)) {
      return {
        kpis: [],
        tableData: []
      };
    }

    let quotes = [...quotesData.OutstandingQuotesDetails];

    // Apply filters
    if (selectedOperator !== 'all') {
      quotes = quotes.filter(quote => {
        // Try to match by operator initials or name
        const operator = operators.find(op => op.Inits === selectedOperator || op.Name === selectedOperator);
        if (!operator) return false;
        // Check if quote has operator/rep field (may vary by API response)
        return quote.Rep === operator.Inits || 
               quote.Operator === operator.Inits ||
               quote.Inits === operator.Inits ||
               quote.OperatorName === operator.Name;
      });
    }

    if (selectedBranch !== 'all') {
      quotes = quotes.filter(quote => quote.Branch === selectedBranch);
    }

    if (selectedStatus !== 'all') {
      quotes = quotes.filter(quote => {
        const status = quote.Status || '';
        return status.toLowerCase() === selectedStatus.toLowerCase();
      });
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      quotes = quotes.filter(quote => {
        const customerName = (quote.CustomerName || '').toLowerCase();
        const document = (quote.Document || '').toLowerCase();
        return customerName.includes(searchLower) || document.includes(searchLower);
      });
    }

    // Calculate KPIs
    const totalQuotes = quotes.length;
    const totalValue = quotes.reduce((sum, quote) => sum + (parseFloat(quote.TotalValue || quote.Goods || 0)), 0);
    const avgValue = totalQuotes > 0 ? totalValue / totalQuotes : 0;

    // Group by status
    const statusGroups = quotes.reduce((acc, quote) => {
      const status = (quote.Status || 'Unknown').toLowerCase();
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
    const pendingCount = statusGroups.pending || 0;
    const sentCount = statusGroups.sent || 0;
    const convertedCount = statusGroups.converted || 0;

    // Group by branch
    const branchGroups = quotes.reduce((acc, quote) => {
      const branch = quote.Branch || 'Unknown';
      acc[branch] = (acc[branch] || 0) + 1;
      return acc;
    }, {});
    const branchCount = Object.keys(branchGroups).length;

    // Group by operator (if available)
    const operatorGroups = quotes.reduce((acc, quote) => {
      const operator = quote.Rep || quote.Operator || quote.Inits || 'Unknown';
      acc[operator] = (acc[operator] || 0) + 1;
      return acc;
    }, {});
    const operatorCount = Object.keys(operatorGroups).length;

    // Age categories
    const ageCategories = quotes.reduce((acc, quote) => {
      const quoteDate = new Date(quote.DateTime);
      const category = getQuoteCategory(quoteDate);
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {});
    const greenCount = ageCategories.green || 0;
    const orangeCount = ageCategories.orange || 0;
    const redCount = ageCategories.red || 0;
    const greyCount = ageCategories.grey || 0;

    // Prepare table data
    const tableData = quotes.map((quote, index) => {
      const quoteDate = new Date(quote.DateTime);
      const category = getQuoteCategory(quoteDate);
      
      return {
        id: quote.Document || index,
        document: quote.Document || 'N/A',
        dateTime: quote.DateTime,
        customerName: quote.CustomerName || 'N/A',
        branch: quote.Branch || 'N/A',
        operator: quote.Rep || quote.Operator || quote.Inits || 'N/A',
        status: quote.Status || 'Unknown',
        partCount: quote.PartCount || 0,
        totalValue: parseFloat(quote.TotalValue || quote.Goods || 0),
        ageCategory: category,
        formattedDate: format(quoteDate, 'dd/MM/yyyy HH:mm')
      };
    }).sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime)); // Sort by date, newest first

    const kpis = [
      {
        key: 'totalQuotes',
        label: 'Total Outstanding Quotes',
        value: totalQuotes,
        type: 'customers',
        format: 'number',
        color: 'text-blue-600'
      },
      {
        key: 'totalValue',
        label: 'Total Quote Value',
        value: totalValue,
        type: 'sales',
        format: 'currency',
        color: 'text-green-600'
      },
      {
        key: 'avgValue',
        label: 'Average Quote Value',
        value: avgValue,
        type: 'sales',
        format: 'currency',
        color: 'text-purple-600'
      },
      {
        key: 'pending',
        label: 'Pending Quotes',
        value: pendingCount,
        type: 'customers',
        format: 'number',
        color: 'text-orange-600'
      },
      {
        key: 'sent',
        label: 'Sent Quotes',
        value: sentCount,
        type: 'customers',
        format: 'number',
        color: 'text-blue-600'
      },
      {
        key: 'converted',
        label: 'Converted Quotes',
        value: convertedCount,
        type: 'customers',
        format: 'number',
        color: 'text-green-600'
      },
      {
        key: 'branches',
        label: 'Branches',
        value: branchCount,
        type: 'branches',
        format: 'number',
        color: 'text-indigo-600'
      },
      {
        key: 'operators',
        label: 'Operators',
        value: operatorCount,
        type: 'advisors',
        format: 'number',
        color: 'text-purple-600'
      }
    ];

    return { kpis, tableData };
  }, [quotesData, selectedOperator, selectedBranch, selectedStatus, searchTerm, operators]);

  // Export functions
  const handleExportPDF = () => {
    exportToPDF({
      title: 'Quotes Report',
      filename: 'quotes-report.pdf',
      tableData: processedData.tableData,
      columns: [
        { header: 'Document', key: 'document' },
        { header: 'Date/Time', key: 'formattedDate' },
        { header: 'Customer', key: 'customerName' },
        { header: 'Branch', key: 'branch' },
        { header: 'Operator', key: 'operator' },
        { header: 'Status', key: 'status' },
        { header: 'Parts', key: 'partCount' },
        { header: 'Value', key: 'totalValue', format: 'currency' }
      ],
      kpis: processedData.kpis,
      subtitle: 'View and analyze outstanding quotes across all branches and operators'
    });
  };

  const handleExportExcel = () => {
    exportToExcel(processedData.tableData, {
      title: 'Quotes Report',
      filename: 'quotes-report.xlsx',
      columns: [
        { header: 'Document', key: 'document' },
        { header: 'Date/Time', key: 'formattedDate' },
        { header: 'Customer', key: 'customerName' },
        { header: 'Branch', key: 'branch' },
        { header: 'Operator', key: 'operator' },
        { header: 'Status', key: 'status' },
        { header: 'Parts', key: 'partCount' },
        { header: 'Value', key: 'totalValue', format: 'currency' }
      ],
      kpis: processedData.kpis
    });
  };

  // Table columns
  const columns = [
    {
      key: 'document',
      label: 'Document',
      sortable: true,
      render: (value) => (
        <span className="font-medium text-blue-600">{value}</span>
      )
    },
    {
      key: 'formattedDate',
      label: 'Date/Time',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="text-sm text-gray-900">{value}</div>
          <div className="text-xs text-gray-500">
            {(() => {
              const category = row.ageCategory;
              const labels = {
                green: '< 1 Hour',
                orange: '< 2 Hours',
                red: '> 2 Hours',
                grey: 'Older'
              };
              return labels[category] || '';
            })()}
          </div>
        </div>
      )
    },
    {
      key: 'customerName',
      label: 'Customer',
      sortable: true,
      render: (value) => (
        <span className="text-gray-900">{value}</span>
      )
    },
    {
      key: 'branch',
      label: 'Branch',
      sortable: true,
      render: (value) => (
        <span className="text-gray-700">{value}</span>
      )
    },
    {
      key: 'operator',
      label: 'Operator',
      sortable: true,
      render: (value) => (
        <span className="text-gray-700">{value}</span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const statusColors = {
          pending: 'yellow',
          sent: 'blue',
          converted: 'green',
          unknown: 'gray'
        };
        const color = statusColors[value?.toLowerCase()] || 'gray';
        return (
          <Badge color={color} variant="light">
            {value || 'Unknown'}
          </Badge>
        );
      }
    },
    {
      key: 'partCount',
      label: 'Parts',
      sortable: true,
      render: (value) => (
        <span className="text-gray-700">{value}</span>
      )
    },
    {
      key: 'totalValue',
      label: 'Value',
      sortable: true,
      type: 'currency',
      render: (value) => (
        <span className="font-semibold text-gray-900">{formatCurrency(value)}</span>
      )
    },
    {
      key: 'ageCategory',
      label: 'Age',
      sortable: true,
      render: (value) => {
        const categoryColors = {
          green: 'bg-green-100 text-green-800',
          orange: 'bg-orange-100 text-orange-800',
          red: 'bg-red-100 text-red-800',
          grey: 'bg-gray-100 text-gray-800'
        };
        const labels = {
          green: '< 1 Hour',
          orange: '< 2 Hours',
          red: '> 2 Hours',
          grey: 'Older'
        };
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${categoryColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {labels[value] || value}
          </span>
        );
      }
    }
  ];

  return (
    <ReportLayout
      title="Quotes Report"
      subtitle="View and analyze outstanding quotes across all branches and operators"
      icon={FileText}
      iconColor="purple"
      onExportPDF={handleExportPDF}
      onExportExcel={handleExportExcel}
      loading={quotesLoading || operatorsLoading}
    >
      {/* KPI Cards */}
      <ReportKPICards kpis={processedData.kpis} loading={quotesLoading} />

      {/* Filters Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center space-x-2 mb-4">
          <Filter className="w-5 h-5 text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Operator</label>
            <Select
              placeholder="All Operators"
              value={selectedOperator}
              onChange={setSelectedOperator}
              data={[
                { value: 'all', label: 'All Operators' },
                ...operators
                  .filter(op => {
                    // Filter out operators with no valid identifier
                    const hasInits = op.Inits && op.Inits.trim() && op.Inits.trim().toUpperCase() !== 'NONE';
                    const hasName = op.Name && op.Name.trim() && op.Name.trim().toUpperCase() !== 'NONE';
                    return hasInits || hasName;
                  })
                  .reduce((acc, op, index) => {
                    // Prefer Inits, fallback to Name, or use index as last resort
                    let value = op.Inits?.trim() || op.Name?.trim() || `operator-${index}`;
                    // Make value unique if it already exists
                    let uniqueValue = value;
                    let counter = 1;
                    while (acc.find(item => item.value === uniqueValue)) {
                      uniqueValue = `${value}-${counter}`;
                      counter++;
                    }
                    
                    const label = `${op.Inits || ''}${op.Inits && op.Name ? ' - ' : ''}${op.Name || ''}`.trim() || value;
                    acc.push({
                      value: uniqueValue,
                      label: label
                    });
                    return acc;
                  }, [])
                  .sort((a, b) => a.label.localeCompare(b.label))
              ]}
              clearable
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Branch</label>
            <Select
              placeholder="All Branches"
              value={selectedBranch}
              onChange={setSelectedBranch}
              data={[
                { value: 'all', label: 'All Branches' },
                ...(quotesData?.OutstandingQuotesDetails || [])
                  .reduce((acc, quote) => {
                    const branch = quote.Branch?.trim();
                    // Filter out empty, null, undefined, or "NONE" branches
                    if (branch && branch.toUpperCase() !== 'NONE') {
                      // Check if we already have this branch to avoid duplicates
                      if (!acc.find(b => b.value === branch)) {
                        acc.push({ value: branch, label: branch });
                      }
                    }
                    return acc;
                  }, [])
                  .sort((a, b) => a.label.localeCompare(b.label))
              ]}
              clearable
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <Select
              placeholder="All Statuses"
              value={selectedStatus}
              onChange={setSelectedStatus}
              data={[
                { value: 'all', label: 'All Statuses' },
                { value: 'pending', label: 'Pending' },
                { value: 'sent', label: 'Sent' },
                { value: 'converted', label: 'Converted' }
              ]}
              clearable
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <TextInput
              placeholder="Search by customer or document..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              leftSection={<MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />}
            />
          </div>
        </div>
      </div>

      {/* Quotes Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Outstanding Quotes ({processedData.tableData.length})
          </h2>
        </div>
        <DataTable
          data={processedData.tableData}
          columns={columns}
          loading={quotesLoading}
          searchable={false}
          sortable={true}
          pagination={true}
          pageSize={20}
          emptyMessage="No quotes found matching your filters"
        />
      </div>
    </ReportLayout>
  );
};

export default QuotesReport;

